import React, { useEffect, useState } from "react";
import { Users } from "lucide-react";
import Booth from "./Booth";
import FloorBorder from "./FloorBorder";
import BoothModal from "./BoothModal";
import axios from "axios";

const FloorPlanAustralia = () => {
  const [selectedBooth, setSelectedBooth] = React.useState(null);
  const [reservedBooths, setReservedBooths] = useState([]);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/floorplanList`)
      .then((res) => {
        const tickets = res.data?.details?.tickets?.data || [];

        const reserved = tickets
          .filter((t) => t.boothno)
          .map((t) => ({
            boothNo: String(t.boothno),
            companyName: t.company || "",
            // ✅ Set default placeholder if logo is missing
            logo: t.company_logo || `${process.env.PUBLIC_URL}/assets/images/booth-reserved/v-process.png`,
            url: t.company_url || "#",
            title: t.boothtitle || "Reserved Booth",
            size: t.boothsize || "",
            // ✅ Optional: you can track approval status
            approved: !!t.company_logo // if logo exists, assume approved
          }));

        setReservedBooths(reserved);
      })
      .catch((err) => {
        console.error("Error fetching floorplan:", err);
      });
  }, []);

  const getReservedInfo = (boothNo, boothId) =>
    reservedBooths.find(
      (b) =>
        (boothNo && b.boothNo === String(boothNo)) ||
        (boothId && b.boothId === boothId)
    );

  const handleReserve = ({ name, company, email, phone }) => {
    const boothNo = selectedBooth?.boothNo ? String(selectedBooth.boothNo) : "";
    const boothId = selectedBooth?.boothId || "";

    if (!boothNo && !boothId) {
      setSelectedBooth(null);
      return;
    }

    const reservedInfo = {
      boothNo,
      boothId,
      companyName: company || name || "Reserved Booth",
      logo: `${process.env.PUBLIC_URL}/assets/images/booth-reserved/v-process.png`,
      url: "#",
      title: selectedBooth?.title || "Reserved Booth",
      size: selectedBooth?.size || "",
      email: email || "",
      phone: phone || "",
      approved: false,
    };

    setReservedBooths((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      const boothExists = list.some((booth) =>
        boothNo ? booth.boothNo === boothNo : booth.boothId === boothId
      );

      if (boothExists) {
        return list.map((booth) =>
          (boothNo ? booth.boothNo === boothNo : booth.boothId === boothId)
            ? { ...booth, ...reservedInfo }
            : booth
        );
      }

      return [...list, reservedInfo];
    });

    setSelectedBooth(null);
  };

  const DoorIcons = ({ x, y }) => (
    <g>
      <rect x={x} y={y} width={4} height={10} fill="#6ee7e0" stroke="#1f2937" strokeWidth={0.8} />
      <rect x={x + 9} y={y} width={4} height={10} fill="#6ee7e0" stroke="#1f2937" strokeWidth={0.8} />
      <circle cx={x + 2} cy={y - 3} r={2.3} fill="#ffffff" stroke="#1f2937" strokeWidth={0.7} />
      <circle cx={x + 11} cy={y - 3} r={2.3} fill="#ffffff" stroke="#1f2937" strokeWidth={0.7} />
    </g>
  );

  const ZoneEntrance = ({ x, y }) => (
    <g>
      <rect x={x} y={y} width={86} height={18} rx={2} fill="#5b57a3" stroke="#2b2d74" strokeWidth={1} />
      <text x={x + 43} y={y + 12.5} textAnchor="middle" fontSize="8" fill="#fff" fontWeight="700">
        Zone Entrance
      </text>
    </g>
  );

  const PlanExtras = () => (
    <>
      {[330, 650, 990].map((x) => (
        <text key={`corridor-${x}`} x={x} y={680} textAnchor="middle" fontSize="22" fill="#626262" fontWeight="800">
          6 Mtr Corridor
        </text>
      ))}
      <ZoneEntrance x={292} y={605} />
      <ZoneEntrance x={610} y={605} />
      <ZoneEntrance x={925} y={605} />
      <DoorIcons x={324} y={706} />
      <DoorIcons x={612} y={706} />
      <DoorIcons x={920} y={706} />
      <DoorIcons x={1130} y={706} />

    
    </>
  );

  // Layout constants based on grid
  const startX = 50;
  const startY = 50;
  // Arrow constants
  const dx = -95;
  const dy = 18;
  const up = 60;
  // Speaker Hall
  const hallX = 1153;
  const hallY = 452;
  const hallW = 380;
  const hallH = 270;

  // Sponsorship colors matching the reference floor plan
  const colors = {
    exclusive: "#4fc3c8",
    official: "#4d6594",
    title: "#9bd3ae",
    regional: "#00a651",
    premium: "#0093cf",
    diamond: "#f58220",
    gold: "#d5b46b",
    silver: "#d9dcdf",
    speakerHall: "#2B3092",   // Purple/magenta
    networking: "#4dc9ff",
    cafe: "#8b5a2b",          // Brown
    photoWall: "#6c7a89",     // Gray
    photoWallStage: "#F1F3F3",     // Gray
    backdrop: "#d4d8dc87",      // Light gray
    ledWall: "#e74c3c"        // Red
  };

  const bottomZones = [
    {
      name: "Zone (A)",
      startX: 170,
      booths: [
        { number: 1, type: "official", title: "Official\nSponsor" },
        { number: 2, type: "exclusive", title: "Exclusive\nSponsor" },
        { number: 3, type: "title", title: "Title\nSponsor" },
      ],
    },
    {
      name: "Zone (B)",
      startX: 510,
      booths: [
        { number: 4, type: "regional", title: "Regional\nSponsor" },
        { number: 5, type: "official", title: "Official\nSponsor" },
        { number: 6, type: "premium", title: "Premium\nSponsor" },
      ],
    },
    {
      name: "Zone (C)",
      startX: 830,
      booths: [
        { number: 7, type: "premium", title: "Premium\nSponsor" },
        { number: 8, type: "premium", title: "Premium\nSponsor" },
        { number: 9, type: "premium", title: "Premium\nSponsor" },
      ],
    },
  ];

  const diamondZones = [
    { startX: bottomZones[0].startX, boothNumbers: [10, 11, 12, 13, 14, 15] },
    { startX: bottomZones[1].startX, boothNumbers: [16, 17, 18, 19, 20, 21] },
  ];

  const zoneATopBlocks = [
    { startX: bottomZones[0].startX, silverNumbers: [22, 23, 24, 25], goldNumbers: [26, 27] },
    { startX: bottomZones[0].startX + 180, silverNumbers: [28, 29, 30, 31], goldNumbers: [32, 33] },
    { startX: bottomZones[1].startX + 170, silverNumbers: [34, 35, 36, 37], goldNumbers: [38, 39] },
  ];

  const zoneDBlocks = [
    { startX: hallX + 15, topGold: [44, 45], silver: [46, 47, 48, 49], bottomGold: [50, 51] },
    { startX: hallX + 180, topGold: [52, 53], silver: [54, 55, 56, 57], bottomGold: [58, 59] },
  ];

  const zoneDTopRow = [
    { number: 60, type: "gold" },
    { number: 61, type: "silver" },
    { number: 62, type: "silver" },
    { number: 63, type: "silver" },
    { number: 64, type: "gold" },
  ];

  return (

    <>
      <div className="py-5">
        {/* Section Title  */}
        <div className="col-lg-6 mx-auto">
          <div className="title-content text-center mb-4">
            <p className="mb-1 pink">
              Floor Plan
            </p>
            <h2 className="mb-1">
              PROFX EXPO<span className="pink"> <b>AUSTRALIA 2026</b></span>
            </h2>
            <p className="m-0">
              Choose from 4 powerful tiers - designed for trend explorers, skill builders, networkers, and deal-closers.
            </p>
          </div>
        </div>
        {/* Floor Plan Container */}
        <div
          className="mx-auto bg-white rounded shadow-xl"
          style={{
            width: "95%",
            maxWidth: "2000px",
            aspectRatio: "2 / 1",
            padding: "20px",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >

          <svg
            viewBox="0 0 1600 800"
            width="100%"
            height="100%"
            preserveAspectRatio="xMidYMid meet"
            style={{
              background: "#fff",
              // transform: "scale(1.0)",     // increase size here
              transformOrigin: "center",
            }}
          >


            {/* Floor Border */}
            <FloorBorder
              x={10}
              y={10}
              width={1580}
              height={780}
            />
  <PlanExtras />
            {/* Inner fine grid for main floor area */}
            {/* <rect x={startX + 90} y={startY} width="1020" height="900" fill="url(#gridDense)" /> */}





            {/* ===== PRODUCT LAUNCH ZONE ===== */}
            <g>
              <rect
                x={bottomZones[1].startX + 20}
                y={75}
                width={105}
                height={20}
                fill="#1f1f1f"
              />
              <text
                x={bottomZones[1].startX + 72.5}
                y={89}
                textAnchor="middle"
                fontSize="10"
                fill="#ffffff"
                fontWeight="600"
              >
                Back Drop
              </text>
              <rect
                x={bottomZones[1].startX + 20}
                y={95}
                width={105}
                height={136}
                fill={colors.official}
              />
              <text
                x={bottomZones[1].startX + 72.5}
                y={150}
                textAnchor="middle"
                fontSize="13"
                fill="#ffffff"
              >
                <tspan x={bottomZones[1].startX + 72.5} dy="0">Product</tspan>
                <tspan x={bottomZones[1].startX + 72.5} dy="16">Launch</tspan>
                <tspan x={bottomZones[1].startX + 72.5} dy="16">Zone</tspan>
              </text>
            </g>

            {/* ===== SILVER & GOLD SPONSORSHIP BOOTHS - ABOVE ZONES A & B ===== */}
            {zoneATopBlocks.map((block, blockIndex) => (
              <g key={`zone-a-top-block-${blockIndex}`}>
                {block.silverNumbers.map((number, index) => {
                  const reservedInfo = getReservedInfo(number);
                  const boothId = `SILVER-${number}`;

                  return (
                    <Booth
                      key={boothId}
                      boothId={boothId}
                      boothType="silver"
                      boothNo={number}
                      title={"Silver\nSponsor"}
                      size="3 x 3"
                      x={block.startX + (index % 2) * 58}
                      y={75 + Math.floor(index / 2) * 52}
                      width={58}
                      height={52}
                      color={colors.silver}
                      fontSize={9}
                      isReserved={!!reservedInfo}
                      reservedInfo={reservedInfo}
                      onClick={setSelectedBooth}
                    />
                  );
                })}

                {block.goldNumbers.map((number, index) => {
                  const reservedInfo = getReservedInfo(number);
                  const boothId = `GOLD-${number}`;

                  return (
                    <Booth
                      key={boothId}
                      boothId={boothId}
                      boothType="gold"
                      boothNo={number}
                      title={"Gold\nSponsor"}
                      size="3 x 3"
                      x={block.startX + index * 58}
                      y={179}
                      width={58}
                      height={52}
                      color={colors.gold}
                      fontSize={9}
                      isReserved={!!reservedInfo}
                      reservedInfo={reservedInfo}
                      onClick={setSelectedBooth}
                    />
                  );
                })}
              </g>
            ))}

            {/* ===== NETWORKING LOUNGE 3 & SILVER/GOLD BOOTHS - ABOVE ZONE C ===== */}
            
            <Booth
              boothId="SPONSOR-NETWORK-3"
              boothType="networklounge"
              title={"Networking\nLounge (3)"}
              size="4 x 7"
              x={bottomZones[2].startX}
              y={75}
              width={105}
              height={135}
              color={colors.networking}
              textColor="#ffffff"
              fontSize={12}
              isReserved={!!getReservedInfo(null, "SPONSOR-NETWORK-3")}
              reservedInfo={getReservedInfo(null, "SPONSOR-NETWORK-3")}
              onClick={setSelectedBooth}
            />

            {[40, 41].map((number, index) => {
              const reservedInfo = getReservedInfo(number);

              return (
                <Booth
                  key={`SILVER-${number}`}
                  boothId={`SILVER-${number}`}
                  boothType="silver"
                  boothNo={number}
                  title={"Silver\nSponsor"}
                  size="3 x 3"
                  x={bottomZones[2].startX + 155 + index * 58}
                  y={75}
                  width={58}
                  height={52}
                  color={colors.silver}
                  fontSize={9}
                  isReserved={!!reservedInfo}
                  reservedInfo={reservedInfo}
                  onClick={setSelectedBooth}
                />
                
              );
            })}

            {[42, 43].map((number, index) => {
              const reservedInfo = getReservedInfo(number);

              return (
                <Booth
                  key={`GOLD-${number}`}
                  boothId={`GOLD-${number}`}
                  boothType="gold"
                  boothNo={number}
                  title={"Gold\nSponsor"}
                  size="3 x 3"
                  x={bottomZones[2].startX + 155 + index * 58}
                  y={127}
                  width={58}
                  height={52}
                  color={colors.gold}
                  fontSize={9}
                  isReserved={!!reservedInfo}
                  reservedInfo={reservedInfo}
                  onClick={setSelectedBooth}
                />
              );
            })}

            {/* ===== DIAMOND SPONSORSHIP BOOTHS - ZONES A & B ===== */}
            {diamondZones.map((zone, zoneIndex) => (
              <g key={`diamond-zone-${zoneIndex}`}>
                {zone.boothNumbers.map((number, index) => {
                  const reservedInfo = getReservedInfo(number);
                  const boothId = `DIAMOND-${number}`;
                  const column = Math.floor(index / 2);
                  const row = index % 2;

                  return (
                    <Booth
                      key={boothId}
                      boothId={boothId}
                      boothType="diamond"
                      boothNo={number}
                      title={"Diamond\nSponsor"}
                      size="4 x 3"
                      x={zone.startX + column * 105}
                      y={285 + row * 78}
                      width={72}
                      height={78}
                      color={colors.diamond}
                      textColor="#ffffff"
                      fontSize={11}
                      isReserved={!!reservedInfo}
                      reservedInfo={reservedInfo}
                      onClick={setSelectedBooth}
                    />
                  );
                })}
              </g>
            ))}

            {/* ===== TRADING CONTEST ZONE - ABOVE ZONE C ===== */}
            <g>
              <rect
                x={bottomZones[2].startX}
                y={235}
                width={280}
                height={210}
                fill="#d4d6d8"
              />

              <rect
                x={bottomZones[2].startX + 48}
                y={280}
                width={145}
                height={130}
                fill="#e7e9ea"
                stroke="#f8fafc"
                strokeWidth={2}
              />

              <circle
                cx={bottomZones[2].startX + 120}
                cy={345}
                r={38}
                fill="#a6a9ad"
              />

              {[
                [bottomZones[2].startX + 62, 294, 1, 1],
                [bottomZones[2].startX + 179, 294, -1, 1],
                [bottomZones[2].startX + 62, 396, 1, -1],
                [bottomZones[2].startX + 179, 396, -1, -1],
              ].map(([x, y, xDirection, yDirection], index) => (
                <path
                  key={`contest-corner-${index}`}
                  d={`M ${x} ${y + yDirection * 18} V ${y} H ${x + xDirection * 18}`}
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth={7}
                />
              ))}

              <text
                x={bottomZones[2].startX + 220}
                y={345}
                textAnchor="middle"
                fontSize={11}
                fill="#252525"
                fontWeight="600"
                transform={`rotate(90, ${bottomZones[2].startX + 220}, 345)`}
              >
                Trading Contest Zone
              </text>
              <text
                x={bottomZones[2].startX + 240}
                y={345}
                textAnchor="middle"
                fontSize={10}
                fill="#252525"
                transform={`rotate(90, ${bottomZones[2].startX + 240}, 345)`}
              >
                14 x 15
              </text>
            </g>

            {/* ===== Entrance - BOTTOM SPONSORSHIP ZONES ===== */}
            {bottomZones.map((zone) => (
              <g key={zone.name}>
                <text
                  x={zone.startX + 122}
                  y={480}
                  textAnchor="middle"
                  fontSize="22"
                  fill="#555b61"
                  fontWeight="700"
                >
                  {zone.name}
                </text>

                {zone.booths.map((booth, index) => {
                  const reservedInfo = getReservedInfo(booth.number);
                  const boothId = `${booth.type.toUpperCase()}-${booth.number}`;

                  return (
                    <Booth
                      key={boothId}
                      boothId={boothId}
                      boothType={booth.type}
                      boothNo={booth.number}
                      title={booth.title}
                      size="5 x 3"
                      x={zone.startX + index * 105}
                      y={500}
                      width={72}
                      height={90}
                      color={colors[booth.type]}
                      textColor={booth.type === "official" || booth.type === "regional" || booth.type === "premium" ? "#ffffff" : "#1a1a2e"}
                      fontSize={11}
                      isReserved={!!reservedInfo}
                      reservedInfo={reservedInfo}
                      onClick={setSelectedBooth}
                    />
                  );
                })}
              </g>
            ))}

            {/* ===== ZONE D BOOTHS - ABOVE CONFERENCE HALL ===== */}
            {zoneDTopRow.map((booth, index) => {
              const reservedInfo = getReservedInfo(booth.number);
              const boothId = `${booth.type.toUpperCase()}-${booth.number}`;

              return (
                <Booth
                  key={boothId}
                  boothId={boothId}
                  boothType={booth.type}
                  boothNo={booth.number}
                  title={`${booth.type === "gold" ? "Gold" : "Silver"}\nSponsor`}
                  size="3 x 3"
                  x={hallX + 15 + index * 58}
                  y={75}
                  width={58}
                  height={52}
                  color={colors[booth.type]}
                  fontSize={9}
                  isReserved={!!reservedInfo}
                  reservedInfo={reservedInfo}
                  onClick={setSelectedBooth}
                />
              );
            })}

            {zoneDBlocks.map((block, blockIndex) => (
              <g key={`zone-d-block-${blockIndex}`}>
                {block.topGold.map((number, index) => {
                  const reservedInfo = getReservedInfo(number);

                  return (
                    <Booth
                      key={`GOLD-${number}`}
                      boothId={`GOLD-${number}`}
                      boothType="gold"
                      boothNo={number}
                      title={"Gold\nSponsor"}
                      size="3 x 3"
                      x={block.startX + index * 58}
                      y={170}
                      width={58}
                      height={52}
                      color={colors.gold}
                      fontSize={9}
                      isReserved={!!reservedInfo}
                      reservedInfo={reservedInfo}
                      onClick={setSelectedBooth}
                    />
                  );
                })}

                {block.silver.map((number, index) => {
                  const reservedInfo = getReservedInfo(number);

                  return (
                    <Booth
                      key={`SILVER-${number}`}
                      boothId={`SILVER-${number}`}
                      boothType="silver"
                      boothNo={number}
                      title={"Silver\nSponsor"}
                      size="3 x 3"
                      x={block.startX + (index % 2) * 58}
                      y={222 + Math.floor(index / 2) * 52}
                      width={58}
                      height={52}
                      color={colors.silver}
                      fontSize={9}
                      isReserved={!!reservedInfo}
                      reservedInfo={reservedInfo}
                      onClick={setSelectedBooth}
                    />
                  );
                })}

                {block.bottomGold.map((number, index) => {
                  const reservedInfo = getReservedInfo(number);

                  return (
                    <Booth
                      key={`GOLD-${number}`}
                      boothId={`GOLD-${number}`}
                      boothType="gold"
                      boothNo={number}
                      title={"Gold\nSponsor"}
                      size="3 x 3"
                      x={block.startX + index * 58}
                      y={326}
                      width={58}
                      height={52}
                      color={colors.gold}
                      fontSize={9}
                      isReserved={!!reservedInfo}
                      reservedInfo={reservedInfo}
                      onClick={setSelectedBooth}
                    />
                  );
                })}
              </g>
            ))}

            <text
              x={hallX + hallW / 2}
              y={430}
              textAnchor="middle"
              fontSize="22"
              fill="#555b61"
              fontWeight="700"
            >
              Zone (D)
            </text>

           {/* Additional */}
            {/* ===== NETWORKING LOUNGE ===== */}
            <Booth
              boothId="SPONSOR-NETWORK-1"
              boothType="networklounge"
              title={"Networking\nLounge (1)"}
              size="4 x 7"
              x={1470}
              y={startY + 25}
              width={65}
              height={105}
              color={colors.networking}
              textColor="#ffffff"
              fontSize={9}
              isReserved={!!getReservedInfo(null, "SPONSOR-NETWORK-1")}
              reservedInfo={getReservedInfo(null, "SPONSOR-NETWORK-1")}
              onClick={setSelectedBooth}
            />

            <Booth
              boothId="SPONSOR-NETWORK-2"
              boothType="networklounge"
              title={"Networking\nLounge (2)"}
              size="4 x 7"
              x={1470}
              y={startY + 295}
              width={65}
              height={105}
              color={colors.networking}
              textColor="#ffffff"
              fontSize={9}
              isReserved={!!getReservedInfo(null, "SPONSOR-NETWORK-2")}
              reservedInfo={getReservedInfo(null, "SPONSOR-NETWORK-2")}
              onClick={setSelectedBooth}
            />

            {/* LED Exposure Wall - Top Right */}
           
            <g>
              {/* LED Wall Box */}
              <rect
                x={startX + 1067}
                y={startY + 28}
                width={23}
                height={70}
                fill={colors.ledWall}
                rx={3}
              />

              {/* LED Exposure Wall text */}
              <text
                x={startX + 1085}
                y={startY + 54}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="6"
                fill="#fff"
                fontWeight="600"
                transform={`rotate(-90, ${startX + 1085}, ${startY + 65})`}
              >
                LED Exposure Wall
              </text>

              {/* Size text */}
              <text
                x={startX + 1105}
                y={startY + 84}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="7"
                fill="rgba(255,255,255,0.9)"
                transform={`rotate(-90, ${startX + 1085}, ${startY + 85})`}
              >
                2.5w × 3h
              </text>
            </g>

            {/* LED Exposure Wall - Second Right */}
            <g>
              {/* LED Wall Box */}
              <rect
                x={startX + 1067}
                y={startY + 250}
                width={23}
                height={70}
                fill={colors.ledWall}
                rx={3}
              />

              {/* LED Exposure Wall text */}
              <text
                x={startX + 1105}
                y={startY + 293}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="6"
                fill="#fff"
                fontWeight="600"
                transform={`rotate(-90, ${startX + 1085}, ${startY + 305})`}
              >
                LED Exposure Wall
              </text>

              {/* Size text */}
              <text
                x={startX + 1120}
                y={startY + 323}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="7"
                fill="rgba(255,255,255,0.9)"
                transform={`rotate(-90, ${startX + 1085}, ${startY + 325})`}
              >
                2.5w × 3h
              </text>
            </g>

              <g>
              {/* LED Wall Box */}
              <rect
                x={startX + 751}
                y={startY + 23}
                width={23}
                height={70}
                fill={colors.ledWall}
                rx={3}
              />

              {/* LED Exposure Wall text */}
              <text
                x={startX + 751}
                y={startY + 23}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="6"
                fill="#fff"
                fontWeight="600"
                transform={`rotate(-90, ${startX + 772}, ${startY + 33})`}
              >
                LED Exposure Wall
              </text>

              {/* Size text */}
              
            </g>

             <g>
              {/* LED Wall Box */}
              <rect
                x={startX + 435}
                y={startY + 23}
                width={23}
                height={70}
                fill={colors.ledWall}
                rx={3}
              />

              {/* LED Exposure Wall text */}
              <text
                x={startX + 455}
                y={startY + 25}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="6"
                fill="#fff"
                fontWeight="600"
                transform={`rotate(-90, ${startX + 466}, ${startY + 43})`}
              >
                LED Exposure Wall
              </text>

              {/* Size text */}
             
            </g>
            
            {/* ===== LEFT SIDE - Cafe ===== */}

            {/* Cafe */}
            {/* <Booth
              boothId="SPONSOR-CAFE"
              boothType="cafe"
              title="Café"
              size=""
              x={startX - 15}
              y={startY + 350}
              width={70}
              height={90}
              color={colors.cafe}
              textColor="#ffffff"
              fontSize={13}
              onClick={setSelectedBooth}
            /> */}


            {/* Cafe Tables - Only 2 */}
            {/* {[0, 1].map((i) => (
              <g key={`cafe-table-${i}`}>
                <circle
                  cx={startX + 160 + i * 40}  // horizontal spacing
                  cy={startY + 400}           // same row
                  r={10}
                  fill="#d4a574"
                  stroke="#8b5a2b"
                  strokeWidth={1}
                />
                <circle
                  cx={startX + 160 + i * 40}
                  cy={startY + 400}
                  r={4}
                  fill="#8b4513"
                />
              </g>
            ))} */}

            {/* ===== SPEAKER HALL ===== */}
            <g>
              <rect
                x={hallX}
                y={hallY}
                width={hallW}
                height={hallH}
                fill={colors.speakerHall}
              />

              {/* Compact seating blocks with a center aisle */}
              {[0, 1, 2].map((row) =>
                [0, 1, 2, 3, 4, 5].map((col) => {
                  const seatX = hallX + 28 + col * 19;
                  const seatY = hallY + 22 + row * 78;

                  const seats = row === 2 ? [0, 1, 2, 3, 4] : [0, 1, 2, 3];

                  return seats.map((seat) => (
                    <Users
                      key={`hall-left-${row}-${col}-${seat}`}
                      x={seatX}
                      y={seatY + seat * 14}
                      size={11}
                      color="#e0b62e"
                      transform={`rotate(90, ${seatX + 5.5}, ${seatY + seat * 14 + 5.5})`}
                    />
                  ));
                })
              )}

              {[0, 1, 2].map((row) =>
                Array.from({ length: 5 }).map((_, col) => {
                  const seatX = hallX + 222 + col * 19;
                  const seatY = hallY + 22 + row * 78;

                  const seats = row === 2 ? [0, 1, 2, 3, 4] : [0, 1, 2, 3];

                  return seats.map((seat) => (
                    <Users
                      key={`hall-right-${row}-${col}-${seat}`}
                      x={seatX}
                      y={seatY + seat * 14}
                      size={11}
                      color="#e0b62e"
                      transform={`rotate(90, ${seatX + 5.5}, ${seatY + seat * 14 + 5.5})`}
                    />
                  ));
                })
              )}

              {/* Conference hall label in the center aisle */}
              <text
                x={hallX + 186}
                y={hallY + hallH / 2}
                textAnchor="middle"
                fontSize="13"
                fill="#ffffff"
                fontWeight="600"
                transform={`rotate(90, ${hallX + 186}, ${hallY + hallH / 2})`}
              >
                Conference Hall (1) 15 x 21
              </text>

              {/* Plain speaker stage */}
              <rect
                x={hallX + hallW - 42}
                y={hallY + 14}
                width={30}
                height={hallH - 28}
                fill="#c7a52c"
              />
              <text
                x={hallX + hallW - 27}
                y={hallY + hallH / 2}
                textAnchor="middle"
                fontSize="12"
                fill="#2f2f2f"
                fontWeight="600"
                transform={`rotate(90, ${hallX + hallW - 27}, ${hallY + hallH / 2})`}
              >
                Speaker Stage
              </text>

            </g>

          </svg>
        </div>
        {/* Footer */}
        <div className="text-center mt-4 text-slate-400 text-sm">
          <p>Hover over any booth for details and to reserve • © PROFX EXPO AUSTRALIA 2026</p>
        </div>
      </div>

      {selectedBooth && (
        <BoothModal
          booth={selectedBooth}
          onClose={() => setSelectedBooth(null)}
          onReserve={handleReserve}
        />
      )}


    </>
  );
};

export default FloorPlanAustralia;
